import {
    backgroundCheckColumns,
    BackgroundCheckColumns,
    BackgroundCheckData,
} from '@/backend/features/background-check/shared/entities/background-check/types';
import { getExtendedAccountStorage } from '@/backend/features/contact-management/utils/getExtendedAccountStorage';
import { getAccountStorage } from '@juvare/contact-management/backend/features/accounts';
import { administratorAccountEmail } from '@juvare/credentialing/backend/constants';
import { defineEntity } from '@juvare/platform-app/App';
import { sql } from '@juvare/platform-app/Integration';
import { AppState, UserState } from '@juvare/platform-sdk-react/types';
import { createHistory } from '../../background-tasks';
import { BackgroundCheckStatus, BackgroundCheckType } from '../../types';
import {
    BackgroundCheckHistoryColumns,
    BackgroundCheckHistoryData,
    backgroundCheckHistoryEntityName,
} from '../background-check-history-data';
import { recalculateBackgroundCheckStatus } from './utils';

export const backgroundCheckEntity = defineEntity<BackgroundCheckData, BackgroundCheckColumns, AppState, UserState>({
    version: 2,
    columns: backgroundCheckColumns,
    migrations: {
        'v1:2025-05-29:initial': ({ dbClient }) => {
            // Create cache columns
            dbClient.update(sql`ALTER TABLE background_check_cache ADD COLUMN "account_id" UUID NOT NULL`);
            dbClient.update(
                sql`CREATE INDEX IF NOT EXISTS "ix_background_check_cache_account_id" ON background_check_cache ("account_id")`,
            );

            dbClient.update(sql`ALTER TABLE background_check_cache ADD COLUMN "status" TEXT NOT NULL`);
            dbClient.update(sql`CREATE INDEX IF NOT EXISTS "ix_background_check_cache_status" ON background_check_cache (LOWER("status"))`);

            dbClient.update(sql`ALTER TABLE background_check_cache ADD COLUMN "type" TEXT NOT NULL`);
            dbClient.update(sql`CREATE INDEX IF NOT EXISTS "ix_background_check_cache_type" ON background_check_cache (LOWER("type"))`);

            dbClient.update(sql`ALTER TABLE background_check_cache ADD COLUMN "updated_at" DOUBLE PRECISION NOT NULL`);
            dbClient.update(
                sql`CREATE INDEX IF NOT EXISTS "ix_background_check_cache_updated_at" ON background_check_cache ("updated_at")`,
            );

            dbClient.update(sql`ALTER TABLE background_check_cache ADD COLUMN "consent" BOOLEAN NOT NULL`);
            dbClient.update(sql`CREATE INDEX IF NOT EXISTS "ix_background_check_cache_consent" ON background_check_cache ("consent")`);

            // Populate the data
            dbClient.update(sql`
                INSERT INTO 
                    background_check_data (
                       _id, 
                       _updated_by,
                       _updated,
                       _created_by,
                       _created,
                       _json
                    )
                SELECT
                    gen_random_uuid(),  -- generates a UUID for _id
                    acc._updated_by,
                    acc._updated,
                    acc._created_by,
                    acc._created,
                    jsonb_build_object(
                               'accountId',        acc._id,
                               'type',              bc.key,
                               'status',            bc.value -> 'status',
                               'updatedAt',        bc.value -> 'updatedAt',
                               'consent',          COALESCE((acc._json ->> 'consentBackgroundCheck')::boolean, false)
                       )
                FROM account_data acc,
                     jsonb_each(acc._json->'backgroundCheck') AS bc(key, value);
            `);

            // Populate the cache columns
            dbClient.update(sql`
                INSERT INTO 
                    background_check_cache (
                        _id, 
                        _updated_by, 
                        _updated, 
                        _created_by, 
                        _created,
                        account_id, 
                        type,
                        status, 
                        updated_at,
                        consent
                    )
                SELECT
                    bcd._id,
                    bcd._updated_by,
                    bcd._updated,
                    bcd._created_by,
                    bcd._created,
                    (bcd._json ->> 'accountId')::uuid,
                    bcd._json ->> 'type',
                    bcd._json ->> 'status',
                    (bcd._json ->> 'updatedAt')::bigint,
                    (bcd._json ->> 'consent')::bool
                FROM background_check_data bcd
            `);
        },
    },
    onChangedAsync: (context, params) => {
        const accountStorage = getAccountStorage(context);

        const accountData =
            params.action === 'create' || params.action === 'update' ? accountStorage.get(params.data.accountId) : undefined;

        if (params.action === 'update' && accountData?.columns.systemStatus === 'deleted') {
            try {
                const historyStorage = context.storage<BackgroundCheckHistoryData, BackgroundCheckHistoryColumns>(
                    backgroundCheckHistoryEntityName,
                );

                const historyRows = historyStorage.list({
                    filter: [
                        {
                            column: 'checkResponseByRequestId',
                            op: 'is-not-null',
                        },
                        {
                            column: 'accountId',
                            op: '=',
                            value: accountData?.id,
                        },
                    ],
                }).rows;

                if (historyRows.length !== 0) {
                    historyRows.forEach((historyRow) => {
                        createHistory({
                            historyStorage,
                            type: params.data.type as BackgroundCheckType,
                            accountId: historyRow.columns.accountId,
                            backgroundCheckStatus: params.data.status,
                            nextStatus: 'processingError',
                            message: 'User deleted',
                            stopCheckingForResultsHistoryId: historyRow.id,
                        });
                    });
                }
            } catch (e) {
                console.log('Unknown error occurred while updating history to stop background check task');
            }
        }
    },
    onAuthPost: (appState, userState, params) => {
        // During client migration, we need to bypass standard authorization checks to manage data as needed.
        // This is only permitted for the administrator account.
        if (userState.email === administratorAccountEmail) {
            return params.action === 'delete' ? params.prevData : params.data;
        }

        if (params.action === 'create' || params.action === 'update') {
            delete params.data.internalBackgroundCheckChange;
        }

        // we only change status through platform function, so no need to check permissions
        if (params.action === 'update') {
            params.data.status = params.prevData.status;
        }

        if (params.action === 'create' || params.action === 'update') {
            return params.data;
        }

        return;
    },
    onTransform: (context, params) => {
        const accountStorage = getExtendedAccountStorage(context);
        const accountData = accountStorage.get(params.data.accountId);

        const internalChange = params.data.internalBackgroundCheckChange;

        if (params.action === 'create' && !internalChange) {
            const backgroundCheckData = recalculateBackgroundCheckStatus(
                context,
                {
                    ...params.data,
                    updatedAt: params.data.updatedAt ?? 0,
                    consent: Boolean(params.data.consent),
                },
                accountData.data,
            );

            params.data = { ...params.data, ...backgroundCheckData };
        }

        if (internalChange) {
            if (internalChange.isManualChange) {
                params.data.type = internalChange.type;
                params.data.status = internalChange.status;
                params.data.updatedAt = internalChange.updatedAt;
            } else if (params.data.type === internalChange.type) {
                params.data.status = internalChange.status;
                params.data.updatedAt = internalChange.updatedAt;
            }

            delete params.data.internalBackgroundCheckChange;
        } else {
            if (params.action === 'update') {
                const handleStatusList: Array<BackgroundCheckStatus> = ['didNotConsent', 'formIncomplete', 'readyToSubmit'];

                if (
                    handleStatusList.includes(params.data.status as BackgroundCheckStatus) ||
                    params.prevData.consent !== params.data.consent
                ) {
                    const backgroundCheckData = recalculateBackgroundCheckStatus(
                        context,
                        {
                            ...params.data,
                            updatedAt: params.data.updatedAt ?? 0,
                            consent: Boolean(params.data.consent),
                        },
                        accountData.data,
                    );

                    params.data = { ...params.data, ...backgroundCheckData };
                }
            }
        }

        return {
            data: params.data,
            columns: {
                accountId: params.data.accountId,
                status: params.data.status,
                type: params.data.type,
                updatedAt: params.data.updatedAt ?? Date.now(),
                consent: Boolean(params.data.consent),
            },
        };
    },
});
